import { onBeforeUnmount, onMounted, ref } from "vue";

export interface MultiselectItemIdAndStatus {
  id: string;
  isChecked: boolean;
}

/**
 *
 * @param multiSelectTimeWindowMs - Timeout within which the shift must be taken into account when selecting thumbnails
 */
export function useMultiselectWithShift(multiSelectTimeWindowMs = 1200) {
  // Detect is "Shift" pressed ==================================================
  const isShiftPressed = ref<boolean>(false);

  function detectShiftPress(event) {
    if (event.type === "keydown" && event.key?.toLowerCase() === "shift") {
      isShiftPressed.value = true;
    } else if (event.type === "keyup" && event.key?.toLowerCase() === "shift") {
      isShiftPressed.value = false;
    }
  }

  onMounted(() => {
    document.addEventListener("keydown", detectShiftPress);
    document.addEventListener("keyup", detectShiftPress);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", detectShiftPress);
    document.removeEventListener("keyup", detectShiftPress);
  });

  // Check/uncheck thumbnails ===================================================
  let lastCheckTimestamp = Date.now();

  class LastTwoSelectedItemIdsQueue {
    ids: Array<string> = [];

    addId(id: string) {
      if (this.ids.length === 0) {
        this.ids.push(null);
      }

      this.ids.push(id);

      if (this.ids.length > 2) {
        this.ids = this.ids.slice(-2);
      }
    }
  }
  const lastTwoSelectedItemIdsQueue = new LastTwoSelectedItemIdsQueue();

  function checkUncheckItem(checkboxEvent: Event, itemId: string, allItemIdsSet: Array<string>, selectedItemIds: Array<string>): Array<MultiselectItemIdAndStatus> {
    let itemIdAndStatusList: Array<MultiselectItemIdAndStatus> = [];

    const isCurrentAssetChecked = (checkboxEvent.target as HTMLInputElement).checked;
    itemIdAndStatusList.push({ id: itemId, isChecked: isCurrentAssetChecked });

    if (isCurrentAssetChecked) {
      lastTwoSelectedItemIdsQueue.addId(itemId);
    }

    if (isShiftPressed.value && isCurrentAssetChecked) {
      const itemsAffectedByMultipleSelection = checkMultipleItems(itemId, allItemIdsSet, selectedItemIds);
      itemIdAndStatusList = itemIdAndStatusList.concat(itemsAffectedByMultipleSelection);
    }

    if (isCurrentAssetChecked) {
      lastCheckTimestamp = Date.now();
    }

    return itemIdAndStatusList;
  }

  function checkMultipleItems(itemId: string, allItemIdsSet: Array<string>, selectedItemIds: Array<string>): Array<MultiselectItemIdAndStatus> {
    const isPrevFileFromSameSet = allItemIdsSet.includes(lastTwoSelectedItemIdsQueue.ids[0]);
    const isWithinMultiselectWindow = Date.now() - lastCheckTimestamp <= multiSelectTimeWindowMs;

    if (isPrevFileFromSameSet && isWithinMultiselectWindow) {
      const prevAssetIndex = allItemIdsSet.findIndex(a => a === lastTwoSelectedItemIdsQueue.ids[0]);
      const currentAssetIndex = allItemIdsSet.findIndex(a => a === lastTwoSelectedItemIdsQueue.ids[1]);

      const smallestIndex = Math.min(prevAssetIndex, currentAssetIndex);
      const biggestIndex = Math.max(prevAssetIndex, currentAssetIndex);

      const allUnselectedBetweenLastAndCurrent = allItemIdsSet.slice(smallestIndex + 1, biggestIndex).filter(f => !selectedItemIds.includes(f));
      return allUnselectedBetweenLastAndCurrent.map(id => ({ id, isChecked: true }));
    }

    return [];
  }

  return {
    defineWhichItemsToCheckUncheck: checkUncheckItem,
  };
}
